import axios from 'axios';
import CoreBase from './engines/CoreBase';

const _name = 'HttpAgent';

const _delay = (ms) => new Promise((r) => setTimeout(r, ms));
const _shortUrl = (url) => url.replace(/^https?:\/\/[^/]+\//, '/').split('?')[0];

/**
 * HttpAgent — thin axios wrapper with 401 retry.
 * On 401: retries up to 3x with fresh Clerk token and increasing delay.
 * Singleton — import agent from './agent'.
 */
class HttpAgent extends CoreBase {
    constructor() {
        super();
        this._name = _name;
    }

    // ─── private ──────────────────────────────────────────

    _refreshConfig = async (config) => {
        try {
            const token = await window.Clerk?.session?.getToken();
            if (token && config?.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } catch (error) {
            this.logError('_refreshConfig', error.message);
        }
        return config;
    };

    _retry = async (verb, args, url, attempts = 3) => {
        const short = _shortUrl(url);

        // Signed out: a 401 can't recover, skip retries and the error popup
        if (!window.Clerk?.session) {
            this.logDebug('_retry', `no session — skip retry ${short}`);
            return null;
        }

        try {
            for (let i = 0; i < attempts; i++) {
                await _delay(300 * (i + 1));
                const config = args[args.length - 1];
                await this._refreshConfig(config);
                try {
                    const resp = await verb(...args);
                    if (resp?.status !== 401) {
                        this.logDebug('_retry', `*** RETRY SUCCESS AFTER ${i + 1} ATTEMPT(S) — ${short} STATUS ${resp?.status} ***`);
                        return resp;
                    }
                    this.logDebug('_retry', `attempt ${i + 1} still 401 — retrying ${short}`);
                } catch (innerError) {
                    if (innerError.response?.status !== 401) throw innerError;
                    this.logDebug('_retry', `attempt ${i + 1} exception 401 — retrying ${short}`);
                }
            }
            this.logError('_retry', `*** ALL ${attempts} RETRY ATTEMPTS FAILED — ${short} ***`);
            return null;
        } catch (error) {
            this.logError('_retry', error.message);
            return null;
        }
    };

    // ─── public ───────────────────────────────────────────

    setJwt = (token) => {
        try {
            if (token !== undefined) {
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }
        } catch (error) {
            this.logError('setJwt', error.message);
        }
    };

    /**
     *
     * @param {string} url target url
     * @param {any} config axios agent config like headers
     * @param {bool} logError when true do error loging
     * @returns
     */
    get = async (url, config, logError) => {
        try {
            const response = await axios.get(url, config);
            if (response?.status !== 401) return response;
            this.logDebug('get', `401 — retrying ${_shortUrl(url)}`);
            return this._retry((u, c) => axios.get(u, c), [url, config], url);
        } catch (error) {
            if (error.response?.status === 401) {
                this.logDebug('get', `401 exception — retrying ${_shortUrl(url)}`);
                return this._retry((u, c) => axios.get(u, c), [url, config], url);
            }

            if (logError) {
                this.logError('get', error.message);
            }

            return error.response;
        }
    };

    delete = async (url, config) => {
        try {
            const response = await axios.delete(url, config);
            if (response?.status !== 401) return response;
            this.logDebug('delete', `401 — retrying ${_shortUrl(url)}`);
            return this._retry((u, c) => axios.delete(u, c), [url, config], url);
        } catch (error) {
            if (error.response?.status === 401) {
                this.logDebug('delete', `401 exception — retrying ${_shortUrl(url)}`);
                return this._retry((u, c) => axios.delete(u, c), [url, config], url);
            }
            this.logError('delete', error.message);
            return error.response;
        }
    };

    post = async (url, data, config) => {
        try {
            const response = await axios.post(url, data, config);
            if (response?.status !== 401) return response;
            this.logDebug('post', `401 — retrying ${_shortUrl(url)}`);
            return this._retry((u, d, c) => axios.post(u, d, c), [url, data, config], url);
        } catch (error) {
            if (error.response?.status === 401) {
                this.logDebug('post', `401 exception — retrying ${_shortUrl(url)}`);
                return this._retry((u, d, c) => axios.post(u, d, c), [url, data, config], url);
            }
            this.logError('post', error.message);
            return error;
        }
    };

    put = async (url, data, config) => {
        try {
            const response = await axios.put(url, data, config);
            if (response?.status !== 401) return response;
            this.logDebug('put', `401 — retrying ${_shortUrl(url)}`);
            return this._retry((u, d, c) => axios.put(u, d, c), [url, data, config], url);
        } catch (error) {
            if (error.response?.status === 401) {
                this.logDebug('put', `401 exception — retrying ${_shortUrl(url)}`);
                return this._retry((u, d, c) => axios.put(u, d, c), [url, data, config], url);
            }
            this.logError('put', error.message);
            return error.response;
        }
    };

    patch = async (url, data, config) => {
        try {
            const response = await axios.patch(url, data, config);
            if (response?.status !== 401) return response;
            this.logDebug('patch', `401 — retrying ${_shortUrl(url)}`);
            return this._retry((u, d, c) => axios.patch(u, d, c), [url, data, config], url);
        } catch (error) {
            if (error.response?.status === 401) {
                this.logDebug('patch', `401 exception — retrying ${_shortUrl(url)}`);
                return this._retry((u, d, c) => axios.patch(u, d, c), [url, data, config], url);
            }
            this.logError('patch', error.message);
            return error.response;
        }
    };
}

const agent = new HttpAgent();
export default agent;
